import { useState } from 'react'
import moment from 'moment'
import type { Intake } from '../../types'
import { DATETIME_FORMAT } from '../../utils/calc'
import { intakeTotal } from '../../utils/intake'
import NutritionDetails from './NutritionDetails'

interface IntakeCardProps {
  intake: Intake
  id: number
  onEdit: (id: number) => void
  onCopy: (id: number) => void
  onDelete: (id: number) => void
}

export default function IntakeCard({ intake, id, onEdit, onCopy, onDelete }: IntakeCardProps) {
  const [showDetails, setShowDetails] = useState(false)
  const time = moment(intake.datetime, DATETIME_FORMAT).format('h:mm a')
  const total = intakeTotal(intake)
  const multiItem = intake.items.length > 1

  return (
    <div className="card card-intake relative">
      <div className="flex items-start gap-3">
        <span className="badge badge-intake shrink-0 mt-0.5">F</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <div className="flex min-w-0 items-baseline gap-2">
              <h3 className="font-semibold text-[var(--text-primary)] truncate">{intake.name || '—'}</h3>
              {intake.servings > 1 && (
                <span
                  className="shrink-0 self-center rounded-md bg-[var(--accent-orange)] px-1.5 py-0.5 text-xs font-bold text-[var(--bg-primary)]"
                  title={`Consumed ${intake.servings} times`}
                >
                  ×{intake.servings}
                </span>
              )}
            </div>
            <span className="text-xs text-[var(--text-secondary)] shrink-0">{time}</span>
          </div>

          {multiItem && (
            <div className="mt-1 text-xs text-[var(--text-secondary)] truncate">
              {intake.items.map((it) => it.name).join(', ')}
            </div>
          )}

          <div className="mt-1 flex items-start justify-between gap-2">
            <div className="text-sm min-w-0">
              <span className="text-[var(--text-primary)] font-medium">{Math.round(total.calories)} kcal</span>
              <span className="text-[var(--text-secondary)]">
                {' · '}Protein {Math.round(total.protein || 0)}g · Carbs {Math.round(total.carbs || 0)}g · Fat{' '}
                {Math.round(total.fat || 0)}g
              </span>
            </div>
            <button
              className="shrink-0 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              onClick={() => setShowDetails((s) => !s)}
              aria-expanded={showDetails}
            >
              Nutrition {showDetails ? '▴' : '▾'}
            </button>
          </div>

          {showDetails && (
            <div className="mt-1.5">
              <NutritionDetails panel={total} />
            </div>
          )}

          <div className="mt-3 flex gap-3 text-xs">
            <button className="text-[var(--accent-teal)] hover:underline" onClick={() => onEdit(id)}>
              Edit
            </button>
            <button className="text-[var(--text-secondary)] hover:underline" onClick={() => onCopy(id)}>
              Copy
            </button>
            <button className="text-[var(--bac-danger)] hover:underline" onClick={() => onDelete(id)}>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
